import React, { ReactNode, useCallback, useState } from 'react';
import { useTheme, Box } from '@mui/material';
import { useModal, ConfirmationModal } from '@europrocurement/flexy-components';
import {
    type DossierArticle,
    type DossierArticleWithInvoice,
    type DossierValidatorStatus,
    REFRESH_CURRENT_DOSSIER_CTX,
    useCurrentDossierCtx,
    useCurrentDossierCtxDispatch,
    useDossierArticle,
    useDossierProcesses,
    useDossierProcessesActionsAndRender,
    useDossierValidatorsCtx,
} from '@europrocurement/l2d-modules';
import type { IconName } from '@fortawesome/fontawesome-svg-core';
import { enqueueSnackbar } from 'notistack';
import type { NestedDossierProcessesOpenSearch } from '@europrocurement/l2d-domain';
import DossierArticlesProcessesList from '../DossierArticlesProcessesList/DossierArticlesProcessesList';
import { DossierArticleProcessMenuItemProps } from '../DossierArticleProcessMenu/DossierArticleProcessMenu';
import DossierProcessesBulkActionsBar from '../DossierProcessesBulkActionsBar/DossierProcessesBulkActionsBar';
import UpdateValidatorForm from '../UpdateValidatorForm/UpdateValidatorForm';
import useDossierBulkActions from '../../hooks/useDossierBulkActions';

export type DossierArticlesProcessesProps = {
    validatorTypeId: number;
    footer?: ReactNode;
    articles: DossierArticleWithInvoice[];
};

const DossierArticlesProcesses: React.FunctionComponent<DossierArticlesProcessesProps> = function ({
    validatorTypeId,
    footer,
    articles,
}) {
    const { palette } = useTheme();
    const { dossier } = useCurrentDossierCtx();
    const { modalActions } = useModal();
    const dispatch = useCurrentDossierCtxDispatch();
    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    const { deleteArticle } = useDossierArticle();
    const { findProcessByArticle } = useDossierProcesses();
    const { getDossierValidatorById } = useDossierValidatorsCtx();
    const { actionUpdateValidatorStatus, handleUpdateStatusChange } =
        useDossierProcessesActionsAndRender();
    const refreshDossier = useCallback(() => {
        if (dispatch) {
            dispatch({ type: REFRESH_CURRENT_DOSSIER_CTX });
        }
    }, [dispatch]);

    const { canDissociate, canMove, handleDissociate, handleMove } = useDossierBulkActions({
        selectedIds,
        refreshDossier,
    });

    const onRemoveProcess = async (articleId: string) => {
        try {
            await deleteArticle(articleId);
        } catch (e) {
            console.error(e);
            enqueueSnackbar('Une erreur est survenue lros de la suppression de votre démarche', {
                variant: 'error',
            });
        } finally {
            modalActions.close();
        }
    };

    const onStatusChange = async (
        process: NestedDossierProcessesOpenSearch,
        newStatus: DossierValidatorStatus,
    ) => {
        try {
            await handleUpdateStatusChange(process, newStatus.code);
        } catch (e) {
            console.error(e);
            enqueueSnackbar('Une erreur est survenue lors de la mise à jour de votre démarche', {
                variant: 'error',
            });
        } finally {
            modalActions.close();
            refreshDossier();
        }
    };

    const getItemActions = (article: DossierArticle): Array<DossierArticleProcessMenuItemProps> => {
        if (!dossier) return [];
        const actions = [];
        const process = findProcessByArticle(dossier, [validatorTypeId], article.id);
        const statuses = process
            ? getDossierValidatorById(process.validatorId)?.statuses || []
            : [];

        if (process) {
            actions.push(
                actionUpdateValidatorStatus(() => {
                    modalActions.call(
                        <UpdateValidatorForm
                            processing={false}
                            process={process}
                            onStatusChanged={(newStatus) => {
                                onStatusChange(process, newStatus);
                            }}
                            statuses={statuses}
                        />,
                    );
                }),
            );
        }

        actions.push({
            label: 'Supprimer la démarche',
            icon: 'trash',
            onClick: (currentArticle: DossierArticle) => {
                modalActions.call(
                    <ConfirmationModal
                        color={palette.error}
                        messages={{
                            question: 'Confirmer cette action ?',
                            detail: {
                                message: 'Êtes vous sur de vouloir supprimer la démarche ?',
                                severity: 'error',
                            },
                            validation: 'Supprimer',
                        }}
                        actionOnValidation={() => onRemoveProcess(currentArticle.id.toString())}
                        actionOnCancellation={() => modalActions.close()}
                    />,
                );
            },
        });

        if (canMove) {
            actions.push({
                label: 'Déplacer',
                icon: 'file-import' as IconName,
                onClick: () => handleMove([article.id]),
            });
        }
        if (canDissociate) {
            actions.push({
                label: 'Dissocier',
                icon: 'folders' as IconName,
                onClick: () => handleDissociate([article.id]),
            });
        }

        return actions as Array<DossierArticleProcessMenuItemProps>;
    };

    return (
        <Box sx={{ gap: '16px', display: 'grid' }}>
            {dossier && (
                <>
                    <DossierProcessesBulkActionsBar
                        selectedIds={selectedIds}
                        onClearSelection={() => setSelectedIds([])}
                        canDissociate={canDissociate}
                        onDissociate={handleDissociate}
                        canMove={canMove}
                        onMove={handleMove}
                    />
                    <DossierArticlesProcessesList
                        articles={articles}
                        dossier={dossier}
                        validatorTypeId={validatorTypeId}
                        legalAnnoucements={[]}
                        getItemActions={getItemActions}
                        selectedIds={selectedIds}
                        setSelectedIds={setSelectedIds}
                    />
                </>
            )}
            {footer}
        </Box>
    );
};

export default DossierArticlesProcesses;
